import axios from 'axios';
import moment from 'moment';
import { endPoint } from '../../EndPoints/endPoint';

const IMAGE_URL = process.env.REACT_APP_IMAGE_URL || '';

export const DEFAULT_DATE = '01-jan-1900';

export const isEmptyBackendValue = (val) => val === null || val === undefined || String(val).trim() === '' || val === '0';

export const toBackendValue = (val) => (isEmptyBackendValue(val) ? '0' : val);

const isPlaceholderDate = (val) => {
  const parsed = moment(val);
  if (!parsed.isValid()) return false;
  return parsed.format('YYYY-MM-DD') === '1899-12-31' || parsed.format('YYYY-MM-DD') === '1900-01-01';
};

export const toDisplayDate = (val) => {
  if (isEmptyBackendValue(val) || isPlaceholderDate(val)) return null;
  const parsed = moment(val);
  return parsed.isValid() ? parsed.toDate() : null;
};

export const toBackendDate = (val) => {
  if (isEmptyBackendValue(val) || isPlaceholderDate(val)) {
    return DEFAULT_DATE;
  }
  const parsed = moment(val);
  if (!parsed.isValid()) {
    return DEFAULT_DATE;
  }
  return parsed.format('DD-MMM-YYYY').toLowerCase();
};

/** Path from GetDocumentsByCustomerNo (PascalCase) or mapped row fields — not the full URL. */
export const extractDocumentPathFromApi = (item) => {
  if (!item) return '';
  const raw = item.Document_Path ?? item.document_Path ?? item.documentPath ?? item.name ?? item.filePath ?? '';
  if (raw === '0' || raw === null || raw === undefined) return '';
  const path = String(raw).trim();
  return path || '';
};

export const extractDocumentUrlFromApi = (item) => {
  if (!item) return '';
  const raw = item.Url ?? item.url ?? item.fullUrl ?? '';
  if (raw === '0' || raw === null || raw === undefined) return '';
  const url = String(raw).trim();
  return url || '';
};

export const resolveCustomerDocumentUrl = (item) => {
  if (!item) return '';
  if (item.file instanceof File) {
    return URL.createObjectURL(item.file);
  }
  const fullUrl = extractDocumentUrlFromApi(item) || item.url || item.fullUrl || '';
  if (fullUrl && fullUrl !== '0' && /^https?:\/\//i.test(fullUrl)) {
    return fullUrl;
  }
  const path = extractDocumentPathFromApi(item) || item.documentPath || item.name || '';
  if (!path || path === '0') return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = IMAGE_URL.endsWith('/') ? IMAGE_URL : `${IMAGE_URL}/`;
  const relative = path.startsWith('/') ? path.slice(1) : path;
  return `${base}${relative}`;
};

export const canViewCustomerDocument = (item) =>
  Boolean(
    item?.file instanceof File ||
    extractDocumentUrlFromApi(item) ||
    extractDocumentPathFromApi(item) ||
    (item?.documentPath && item.documentPath !== '0') ||
    (item?.url && item.url !== '0') ||
    (item?.name && item.name !== '0'),
  );

export const isCustomerDocumentImage = (item, url = '') => {
  if (item?.file instanceof File) {
    return item.file.type.startsWith('image/');
  }
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(url);
};

export const uploadCustomerDocuments = async (refNo, documentsList) => {
  const pending = (documentsList || []).filter((item) => item?.file instanceof File);
  if (!pending.length) {
    return (documentsList || []).map((item) => ({
      ...item,
      documentPath: item.documentPath || item.name || '0',
      url: item.url || '0',
    }));
  }

  const formData = new FormData();
  pending.forEach(({ file }) => formData.append('files', file));
  formData.append('refNo', String(refNo));
  formData.append('fileCategory', 'Documents');

  const token = localStorage.getItem('Token');
  const db = localStorage.getItem('DataBase');

  const response = await axios.post(endPoint.UNIT_FILE, formData, {
    headers: {
      accept: 'text/plain',
      apikey: 'V+kBloEwqqVho1PINmQI0VJQwSBX2l/e',
      Authorization: `Bearer ${token}`,
      db,
    },
  });

  const results = response?.data?.result || [];
  if (!response?.data?.isSuccessful && !results.length) {
    throw new Error(response?.data?.message || 'Document upload failed');
  }

  let uploadIndex = 0;
  const mappedDocuments = (documentsList || []).map((item) => {
    if (item?.file instanceof File) {
      const uploaded = results[uploadIndex++] || {};
      const filePath = uploaded.filePath || uploaded.fileName || '0';
      const fullUrl = uploaded.fullUrl || '0';
      return { ...item, documentPath: filePath, url: fullUrl, name: filePath, file: null };
    }
    return {
      ...item,
      documentPath: item.documentPath || item.name || '0',
      url: item.url || '0',
    };
  });

  console.log('[Customer Document Upload] mapped document paths', mappedDocuments);
  return mappedDocuments;
};

export const mapCustomerDocumentsPayload = (documentsList, customerNo = '0') =>
  (documentsList || []).map((item) => ({
    customer_No: customerNo || '0',
    document_Type: item?.type?.label || '0',
    document_No: toBackendValue(item?.documentNo),
    document_Desc: toBackendValue(item?.Remarks),
    doc_IssueDate: toBackendDate(item?.IssueDate),
    doc_ExpiryDate: toBackendDate(item?.expiryDate),
    document_Path: item?.documentPath || item?.name || '0',
    url: item?.url || '0',
  }));
